import React from "react";
import { Button, Checkbox } from "antd";
import { Inquiry } from "common/interfaces";
import { Link } from "react-router-dom";
import { UTCToJapanTime } from "common/helper/time.helper";
import { ConversationInquiryTypeText } from "common/enums/ConversationInquiryType.enum";
import { ConversationInquiryStatusText } from "common/enums/ConversationInquiryStatus.enum";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { UserTypeEnum } from "common/enums/UserType.enum";
import { isNew } from "common/helper/status.helper";

interface InquiryItemProps<T extends Inquiry> {
  inquiry: T;
  checked: boolean;
  onSelect: (code: string, checked: boolean) => void;
  hideSelect?: boolean;
}

export function InquiryItem<T extends Inquiry>({
  inquiry,
  checked,
  onSelect,
  hideSelect,
}: InquiryItemProps<T>) {
  const { userData, error } = useSelector((state: RootState) => state.auth);
  const timeJapan = UTCToJapanTime(inquiry.created_at);

  return (
    <div className="self-stretch bg-white rounded-md flex flex-col justify-start items-start overflow-hidden">
      <div
        data-プロパティ1="base + checkbox"
        className="self-stretch min-h-10 px-12 py-2.5 relative bg-primary inline-flex justify-center items-center gap-2.5"
      >
        <div className="flex-1 text-center justify-start text-white text-sm font-bold font-['Noto_Sans_JP'] leading-snug">
          お問い合わせID #{inquiry.code}
        </div>
        {isNew(inquiry.created_at) && (
          <div
            data-プロパティ1="sp - base"
            className="min-w-10 h-[24px] px-[5px] py-[3px] right-[10px] top-[9px] absolute bg-primary-light rounded-md inline-flex flex-col justify-center items-center gap-2.5"
          >
            <div className="text-center justify-start text-primary text-xs font-normal font-['Noto_Sans_JP'] leading-none">
              NEW
            </div>
          </div>
        )}
        <div
          data-プロパティ1="active - border"
          className="w-7 h-7 py-[3px] left-[10px] top-[6px] absolute bg-primary rounded-md inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden"
        >
          {!hideSelect && (
            <Checkbox
              checked={checked}
              onChange={(e) => onSelect(inquiry.code, e.target.checked)}
            />
          )}
        </div>
      </div>
      <div className="self-stretch min-h-10 p-5 bg-white rounded-bl-md rounded-br-md border-l border-r border-b border-neutral-300 flex flex-col justify-center items-center gap-5">
        <div className="self-stretch inline-flex justify-start items-start gap-3.5 flex-wrap content-start">
          <div className="min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              件名
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {inquiry.subject}
            </div>
          </div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]"></div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              種別
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {ConversationInquiryTypeText(inquiry.type)}
            </div>
          </div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              企業
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {inquiry.company.company_name}
            </div>
          </div>
          <div className="flex-1 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              ステータス
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {ConversationInquiryStatusText(inquiry.status)}
            </div>
          </div>
          <div className="flex-1 h-12 min-w-32 inline-flex flex-col justify-start items-start gap-[3px]">
            <div className="self-stretch justify-start text-slate-400 text-[13px] font-normal font-['Noto_Sans_JP'] leading-tight">
              最終更新
            </div>
            <div className="self-stretch justify-start text-black text-[15px] font-normal font-['Noto_Sans_JP'] leading-normal">
              {`${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`}
            </div>
          </div>
        </div>
        <Link
          to={`/${userData.type === UserTypeEnum.COMPANY_SELLER ? "seller" : userData.type === UserTypeEnum.COMPANY_BUYER ? "buyer" : "admin"}/conversation-inquiry/${inquiry.id}`}
          className="w-full"
        >
          <Button
            block
            type="primary"
            htmlType="button"
            className="w-full min-h-[50px] bg-primary-light text-primary text-[16px] font-bold"
          >
            詳細画面へ
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default InquiryItem;
